import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetApartmentDetailsQuery } from "../../../api/apartment/apartmentApiSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { setApartmentDetails } from "../../../redux/slice/apartmentRental/apartmentRentalSlice";
import { setAssetGroup } from "../../../redux/slice/bookingCreation/bookingCreationSlice";
import FullLockerView from "../common/components/locationFromBottomBar/FullLockerView";
import ImageGallery, {
  ImageGalleryImage,
} from "../../../components/ImageGallery";
import LoaderOverlay from "../../layout/components/LoaderOverlay";
import { useTheme } from "@mui/material";
import { formatCurrency } from "../../../util";
import { i18n } from "../../../i18n/i18n";

const ApartmentOverview = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const [openImage, setOpenImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  const { selectedApartment } = useSelector(
    (state: RootState) => state.apartment
  );

  const { data, isLoading } = useGetApartmentDetailsQuery({
    apartmentId: selectedApartment?.assetId ?? "",
  });

  const generalAmenities = useMemo(() => {
    return (
      data?.accomodationdetail?.amenities?.filter(
        (amenity) => amenity.type === "General"
      ) || []
    );
  }, [data]);

  const otherAmenities = useMemo(() => {
    return (
      data?.accomodationdetail?.amenities?.filter(
        (amenity) => amenity.type === "Text"
      ) || []
    );
  }, [data]);

  const handleOpenImage = (image: string) => {
    setSelectedImage(image);
    setOpenImage(true);
  };

  useEffect(() => {
    if (data) {
      dispatch(setApartmentDetails(data));
      dispatch(setAssetGroup({ id: data.groupId }));
    }
  }, [data, dispatch]);

  useEffect(() => {
    // Prevent scrolling on mount
    document.body.style.overflow = "hidden";

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const lang = i18n.language as "en" | "de";

  if (isLoading) {
    return <LoaderOverlay open={isLoading} />;
  }

  return (
    // Removed h-screen and overflow-hidden to allow parent FullScreenDialog to control scrolling
    <div className="max-w-full mx-auto flex flex-col p-4">
      <ImageGallery
        images={
          (data?.additionalImages || []).map((src) => ({
            src,
          })) as ImageGalleryImage[]
        }
        aspectRatio="16/9"
        enableLoop
        className="border border-gray-200"
        onImageClick={(img) => handleOpenImage(img.src)}
      />

  {/* Content now relies on parent container for scrolling (single scrollbar) */}
  <div className="flex-1 mt-4 text-black">
        <h1 className="text-xl font-semibold mb-4">{data?.name}</h1>

        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 text-sm text-gray-600">
            <span>
              {data?.accomodationdetail?.guests} {t("apartment.guests")}
            </span>
            <span>
              • {data?.accomodationdetail?.rooms}{" "}
              {t("apartment.room", {
                count: data?.accomodationdetail?.rooms ?? 1,
              })}
            </span>
            <span>
              • {data?.accomodationdetail?.beds}{" "}
              {t("apartment.bed", {
                count: data?.accomodationdetail?.beds ?? 1,
              })}
            </span>
            <span>
              • {data?.accomodationdetail?.baths}{" "}
              {t("apartment.bath", {
                count: data?.accomodationdetail?.baths ?? 1,
              })}
            </span>
          </div>
          <div className="text-right">
            <span className="mr-1 font-medium">{t("general.from")}</span>
            <span
              style={{ color: theme.palette.input.focus }}
              className="font-medium"
            >
              {formatCurrency(
                data?.Group?.pricecategory?.pricescale?.[0]?.price ?? 0,
                i18n.language
              )}
            </span>
            <span className="text-gray-600 font-medium">
              /{t("apartment.night")}
            </span>
          </div>
        </div>

        <h2 className="text-lg font-semibold mb-2">
          {t("apartment.facility_description_title")}
        </h2>
        <div className="text-sm text-gray-600 mb-6">
          {data?.accomodationdetail?.facilityDescriptions[lang]
            ? data?.accomodationdetail?.facilityDescriptions[lang]
            : data?.accomodationdetail?.facilityDescription}

          {(generalAmenities?.length ?? 0) > 0 && (
            <ul className="list-disc pl-5 mt-3">
              {generalAmenities.map((amenity, index) => (
                <li key={index}>
                  {amenity?.langValues[lang]
                    ? amenity?.langValues[lang]
                    : amenity?.value}
                </li>
              ))}
            </ul>
          )}

          {(otherAmenities?.length ?? 0) > 0 && (
            <div className="mt-3 space-y-3">
              {otherAmenities.map((amenity, index) => (
                <p key={index}>
                  {amenity?.langValues[lang]
                    ? amenity?.langValues[lang]
                    : amenity?.value}
                </p>
              ))}
            </div>
          )}
        </div>

        {(() => {
          const note = data?.accomodationdetail?.importantNotes[lang];
          return typeof note === "string" && note.length > 0 ? (
            <>
              <h2 className="text-lg font-semibold mb-2">
                {t("apartment.important_note")}
              </h2>
              <p className="text-sm text-gray-600 mb-6">{note}</p>
            </>
          ) : null;
        })()}

        <h2 className="text-lg font-semibold mb-2">
          {t("apartment.location")}
        </h2>
        <div className="w-full h-48 rounded-lg overflow-hidden border border-gray-200">
          <iframe
            title="Property Location"
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 0 }}
            src={`https://www.google.com/maps?q=${data?.Group?.location?.address?.geoLat},${data?.Group?.location?.address?.geoLng}&z=15&output=embed`}
            allowFullScreen
          />
        </div>
      </div>

      {/* Full Image View Modal */}
      {selectedImage && openImage && (
        <FullLockerView
          setOpenImage={setOpenImage}
          image={selectedImage}
          openImage={openImage}
        />
      )}
    </div>
  );
};

export default ApartmentOverview;
